import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomValidators } from '@shared/customValidators';
import { collectFormErrors } from '@shared/formHelpers';
import { ExpectedActivitySurvey } from '@shared/models/data-model';
import { AmplitudeService } from '@shared/services/amplitude.service';
import { DTRUM_ACTION_NAME, DynatraceHelperService } from '@shared/services/dynatraceHelper.service';
import { GoogleTagManagerService } from '@shared/services/google-tag-manager.service';
import { RegistrationService } from '@shared/services/registration.service';
import { REGISTRATION_STEP_ID } from '@shared/services/steps.service';
import { NGXLogger } from 'ngx-logger';
import { distinctUntilChanged, map } from 'rxjs/operators';

@Component({
    selector: 'external-deposit',
    templateUrl: './external-deposit.component.html',
    styles: ``
})
export class ExternalDepositComponent implements OnInit {
    private readonly subStepName = 'Expected External Deposit';
    private expectedActivitySurveyToSave: ExpectedActivitySurvey;
    private readonly showDescription = () => {
        if (!this.form.get('description')) {
            this.form.addControl('description', this.fb.control(null, [
                Validators.required,
                Validators.maxLength(this.externalDepositDescriptionMaxlength)
            ]));
        }
    }
    private readonly removeDescription = () => {
        if (this.form.get('description')) {
            this.form.removeControl('description');
        }
    }
    loading = false;
    externalDepositDescriptionMaxlength: number;
    externalDepositOptions = [
        { label: '0-15%', value: 8, toggleDescription: this.removeDescription },
        { label: '15-25%', value: 20, toggleDescription: this.removeDescription },
        { label: '25-50%', value: 38, toggleDescription: this.showDescription },
        { label: 'REGISTRATION.EXPECTED_ACTIVITY_STEP.DEPOSIT.OVER_50_PERCENT', value: 51, toggleDescription: this.showDescription }
    ];
    form: UntypedFormGroup;

    constructor(
        private readonly fb: UntypedFormBuilder,
        private readonly registrationService: RegistrationService,
        private readonly amplitudeService: AmplitudeService,
        private readonly gtmService: GoogleTagManagerService,
        private readonly logger: NGXLogger,
        private readonly router: Router,
        private readonly route: ActivatedRoute,
        private readonly dynatrace: DynatraceHelperService
    ) { }

    ngOnInit(): void {
        this.trackSubStepViewed();
        const expectedActivitySurvey = this.route.snapshot.data.expectedActivitySurvey;
        this.expectedActivitySurveyToSave = {
            ...expectedActivitySurvey
        };
        this.externalDepositDescriptionMaxlength = CustomValidators.getExpectedActivitySurveyMaxlength("thirdPartyMoneyLoadDescription");
        this.form = this.fb.group({
            externalDeposit: [null, Validators.required],
            description: [expectedActivitySurvey.thirdPartyMoneyLoadDescription, [
                Validators.required,
                Validators.maxLength(this.externalDepositDescriptionMaxlength)
            ]]
        });
        this.form.get('externalDeposit').valueChanges.pipe(
            map(value => this.externalDepositOptions.find(option => option.value === value)),
            distinctUntilChanged()
        ).subscribe((option) => {
            option?.toggleDescription()
        });
        this.form.get('externalDeposit').setValue(expectedActivitySurvey.thirdPartyMoneyLoad);
    }


    private trackSubStepViewed(): void {
        this.amplitudeService.trackSubStepViewed(this.subStepName);
        this.gtmService.trackSubStepViewed(this.subStepName);
    }

    private trackSubStepCompleted(): void {
        this.amplitudeService.trackSubStepCompleted(this.subStepName);
        this.gtmService.trackSubStepCompleted(this.subStepName);
    }

    previousStep(): void {
        const backActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.BACK_TO_PREVIOUS);
        this.router.navigate([this.registrationService.getPreviousStep()]).then(() => {
            this.dynatrace.leaveAction(backActionId);
        });
    }

    async nextStep() {
        if (this.form.valid) {
            this.loading = true;

            const submitActionId = this.dynatrace.enterAction(DTRUM_ACTION_NAME.SUBMIT_FORM);

            const formValues = this.form.value;
            this.expectedActivitySurveyToSave.thirdPartyMoneyLoad = formValues.externalDeposit;
            this.expectedActivitySurveyToSave.thirdPartyMoneyLoadDescription = formValues.description ? formValues.description : '';
            try {
                await this.registrationService.saveExpectedActivitySurvey(this.expectedActivitySurveyToSave);
                this.trackSubStepCompleted();
                this.registrationService.completeStep(REGISTRATION_STEP_ID.EXTERNAL_DEPOSIT);
                this.router.navigate([this.registrationService.getNextStep()]).then(() => {
                    this.dynatrace.leaveAction(submitActionId);
                });
            } catch (e) {
                this.logger.error('Save expected activity survey failed', e, 'with expected activity survey', this.expectedActivitySurveyToSave);
                this.loading = false;
                this.dynatrace.leaveAction(submitActionId);
            }
        } else {
            this.form.markAllAsTouched();
            this.amplitudeService.trackFormSubmissionRejected(this.route.snapshot.data['pageName'], collectFormErrors(this.form));
        }
    }

}
